<template>
    <div class="review view-container">
        <div class="review__content">
            <TitleText class="spacing-b-6" level="1">
                {{ $ml.with(0, customer.fullName).get('reviewTitle') }}
            </TitleText>
    
            <ReviewSection
                :title="$ml.get('identificationTitle')"
                @edit="handleEdit('DataOnFileEditCustomer')"
                class="spacing-b-4"
            >
                <span>{{ customer.email }}</span>
                <span>{{ formattedIdentityNumber }}</span>
                <span>{{ formattedPhoneNumber }}</span>
            </ReviewSection>
    
            <ReviewSection
                v-if="productHasShipping"
                :title="$ml.get('shippingSectionTitle')"
                @edit="handleEdit('DataOnFileAddresses')"
                class="spacing-b-4"
            >
                <span>{{ selectedShippingAddress.street + ', ' + selectedShippingAddress.number }}</span>
                <span>{{ selectedShippingAddress.city + ' - ' + selectedShippingAddress.state + ' - ' + formattedZipCode }}</span>
                <span>Frete grátis</span>
            </ReviewSection>
    
            <ReviewSection
                :title="$ml.get('payment')"
                :border-bottom="false"
                @edit="handleChangePaymentType"
                btn-text="change"
            >
                <SelectedCard class="spacing-b-5" />
                
                <div class="inputs-container">
                    <CieloInputV2
                        :autofocus="false"
                        :borderless="false"
                        name="SecurityCode"
                        :label="$ml.get('inputCVV')"
                        v-model="securityCode"
                        v-validate="textValidedCvv"
                        :data-vv-as="$ml.get('securityCode')"
                        :type="'tel'"
                        :error="errors.first('SecurityCode')"
                        :mask="[maskValidedCvv]"
                    />
                    
                    <CieloSelectV2
                        @change="selectItem"
                        :title="$ml.get('installment')"
                        name="selectInstallment"
                        :label="$ml.get('installmentsNumber')"
                        :option-items="installmentList || undefined"
                        :selected-item="numberOfInstallments"
                        :on-get-item-event="getLangOptions"
                        :disabled="!paymentMethodImage || this.installmentIsFixed"
                    />
                </div>
            </ReviewSection>
        </div>

        <ButtonsFinishPaymentV2
            :loading="loading"
            :show-button-previous="false"
            :disable-button-next="disableButtonFinish"
            @click-next="finishPayment"
        />
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';

    import PaymentProcessor from '@/services/payment/PaymentProcessor';

    const CieloInputV2 = () =>
        import(
            /* webpackChunkName: 'cielo-input-v2' */
            '@/components/ClickToPay/template/CieloInputV2'
        );
    import ReviewSection from '../components/ReviewSection';
    import SelectedCard from '../components/SelectedCard';
    import TitleText from '@/components/template/TitleText';
    import CieloSelectV2 from '@/components/ClickToPay/template/CieloSelectV2/CieloSelectV2';
    import ButtonsFinishPaymentV2 from '@/components/template/ButtonsFinishPaymentV2';

    import { formatCpfNumber, formatCnpjNumber, formatPhoneNumber, formatZipCode, } from '@/helpers/stringHelper';
    import { Token, Options } from '@/config/lang/genericOptions.js';
    import ProductHelper from '@/helpers/productHelper';


    export default {
        components: {
            ReviewSection,
            SelectedCard,
            TitleText,
            CieloSelectV2,
            CieloInputV2,
            ButtonsFinishPaymentV2,
        },

        data() {
            return {
                loading: false,
            };
        },

        created() {
            PaymentProcessor.multiLanguage = this.$ml;
        },

        computed: {
            ...mapGetters({
                customer: 'dataOnFile/getCustomer',
                selectedShippingAddress: 'dataOnFile/getSelectedShippingAddress',
                installmentList: 'payment/getInstallmentListForPaymentMethodSelected',
                selectedPaymentMethod: 'payment/getSelectedPaymentMethod',
                installmentIsFixed: 'payment/getSelectedPaymentMethodInstallmentIsAbleToBeFixed',
            }),

            productHasShipping: () => ProductHelper.productHasShipping(),

            securityCode: {
                get: (self) => self.$store.state.cardPayment.SecurityCode,
                set(value) {
                    this.$store.commit('cardPayment/setSecurityCode', value);
                },
            },
            
            numberOfInstallments: {
                get: (self) => self.$store.getters['payment/getInstallmentNumberOfPayments'],
                set(value) {
                    this.$store.commit('payment/setNumberOfInstallments', value);
                },
            },

            maskValidedCvv: (self) => {
                if (self.selectedPaymentMethod) return self.selectedPaymentMethod.Image == 'amex' ? '####' : '###';
            },

            textValidedCvv: (self) => {
                if (self.selectedPaymentMethod) return self.selectedPaymentMethod.Image == 'amex' ? 'required|min:4|max:4' : 'required|min:3|max:4';
            },

            paymentMethodImage: (self) => {
                return self.selectedPaymentMethod ? self.selectedPaymentMethod.Image : null;
            },

            formattedIdentityNumber() {
                const { identityType, identity } = this.customer;
                if (!identity) return '';
                if (identityType === 'CPF' && identity.length === 11) {
                    return formatCpfNumber(identity);
                }
                if (identityType === 'CNPJ' && identity.length === 14) {
                    return formatCnpjNumber(identity);
                }
                return identity;
            },

            formattedPhoneNumber() {
                const phone = this.customer.phone;
                if (!phone) return '';
                if (phone.length === 11) {
                    return formatPhoneNumber(phone);
                }
                return phone;
            },

            formattedZipCode() {
                const zip = this.selectedShippingAddress.zipCode;
                if (!zip) return '';
                if (zip.length === 8) {
                    return formatZipCode(zip);
                }
                return zip;
            },

            disableButtonFinish() {
                const hasErrors = this.errors.items.length > 0;
                const missingCommonFields = !this.securityCode || !this.numberOfInstallments;
                
                return missingCommonFields || hasErrors;
            }
        },

        methods: {
            handleChangePaymentType() {
                this.$store.commit('dataOnFile/setSelectedCardId', null);
                this.$store.commit('dataOnFile/setSelectedBillingAddressId', null);
                this.$store.dispatch('pipelineV2/pushTo', 'ChangePaymentType');
            },

            handleEdit(routeName) {
                this.$store.dispatch('pipelineV2/pushTo', routeName);
            },

            selectItem(item) {
                this.numberOfInstallments = item;
            },

            getLangOptions(item) {
                let tokens = [];

                tokens.push(new Token('quantity', item.Value));
                tokens.push(new Token('installmentPrice', item.installmentPrice));

                return new Options('installmentsItem', tokens);
            },

            async finishPayment() {
                this.loading = true;
                
                try {
                    await PaymentProcessor.Process();
                } catch (error) {
                    console.error('Error during finishPayment:', error);
                } finally {
                    this.loading = false;
                }
            },
        },
    };
</script>

<style scoped lang='scss'>
    .review {
        &__content {
            margin-bottom: 24px;
            
            @media screen and (min-width: 1025px) {
                margin-bottom: 4px;
            }
        }
    }
</style>
